import { Activity, ArrowLeft, Cable, ChevronRight, Eye, KeyRound, Network } from 'lucide-react'
import { FLAGS } from '@atbash-labs/ontology-flags'
import {
  useListExternalMessagingChannelsQuery,
  useListGatewayLinkRoutesQuery,
  useListIntegrationsQuery,
  useListWebDevEndpointsQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { useFlag } from '../../components/flags/useFlag'
import { Card } from '../../components/ui/card'
import { Separator } from '../../components/ui/separator'
import { ConnectionsCheckCard } from './components/ConnectionsCheckCard'
import { ConnectionHealthTab } from './connection-health/ConnectionHealthTab'
import { GatewaysTab } from './gateways/GatewaysTab'
import { GatewayLinkTab } from './gateway-link/GatewayLinkTab'
import { IntegrationsTab } from './integrations/IntegrationsTab'
import { ObserversTab } from './observers/ObserversTab'
import { TagPollingIntervalCard } from './tag-polling/TagPollingIntervalCard'
import type { ConnectivityPanel } from './settings-nav'

function PanelHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="ghost" size="icon-sm" onClick={onBack} aria-label="Back">
        <ArrowLeft className="size-4" />
      </Button>
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
    </div>
  )
}

function ConnectivityHub({
  onOpen
}: {
  onOpen: (panel: Exclude<ConnectivityPanel, 'hub'>) => void
}) {
  const sightingsEnabled = useFlag(FLAGS.RELEASE_SIGHTING_PLANNER)
  const integrationsQuery = useListIntegrationsQuery()
  const channelsQuery = useListExternalMessagingChannelsQuery({
    platform: 'teams',
    page_size: 500
  })
  const gatewaysQuery = useListWebDevEndpointsQuery()
  const gatewayLinkQuery = useListGatewayLinkRoutesQuery()

  const integrationCount = integrationsQuery.data?.length ?? 0
  const teamsCount = channelsQuery.data?.length ?? 0
  const gatewayCount = gatewaysQuery.data?.length ?? 0
  const gatewayLinkCount = gatewayLinkQuery.data?.length ?? 0
  const gatewayLinkOnline = gatewayLinkQuery.data?.filter((route) => route.online).length ?? 0

  const integrationsSummary = [
    integrationCount > 0 ? `${integrationCount} connected` : 'None connected',
    teamsCount > 0 ? `Teams (${teamsCount} channel${teamsCount === 1 ? '' : 's'})` : null
  ]
    .filter(Boolean)
    .join(' · ')

  const gatewaysSummary =
    gatewayCount > 0
      ? `${gatewayCount} Ignition gateway${gatewayCount === 1 ? '' : 's'} · WebDev basic auth`
      : 'No gateways configured · WebDev basic auth'

  return (
    <div className="flex flex-col gap-4">
      <Card className="gap-0 overflow-hidden py-0 shadow-sm">
        <div className="bg-muted/60 px-4 py-2.5 text-11 font-semibold tracking-[0.08em] text-ink-3 uppercase">
          Connectivity
        </div>

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
          onClick={() => onOpen('integrations')}
        >
          <KeyRound className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink">Integrations</div>
            <div className="text-xs font-normal text-ink-3">{integrationsSummary}</div>
          </div>
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        <Separator />

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
          onClick={() => onOpen('gateway-link')}
        >
          <Cable className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink">Gateway Link routing</div>
            <div className="text-xs font-normal text-ink-3">
              {gatewayLinkCount > 0
                ? `${gatewayLinkOnline} of ${gatewayLinkCount} module session${
                    gatewayLinkCount === 1 ? '' : 's'
                  } online`
                : 'WebDev compatibility and per-capability cutover'}
            </div>
          </div>
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        <Separator />

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
          onClick={() => onOpen('gateways')}
        >
          <Network className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink">Gateway credentials</div>
            <div className="text-xs font-normal text-ink-3">{gatewaysSummary}</div>
          </div>
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        <Separator />

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
          onClick={() => onOpen('connection-health')}
        >
          <Activity className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink">Connection health</div>
            <div className="text-xs font-normal text-ink-3">
              Live checks, cadence, and data-path status
            </div>
          </div>
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        {sightingsEnabled ? (
          <>
            <Separator />

            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
              onClick={() => onOpen('observers')}
            >
              <Eye className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
              <div className="min-w-0 flex-1 text-left">
                <div className="text-sm font-medium text-ink">Observers</div>
                <div className="text-xs font-normal text-ink-3">
                  Read-only sighting-capability registry
                </div>
              </div>
              <ChevronRight className="size-4 shrink-0 text-ink-3" />
            </Button>
          </>
        ) : null}
      </Card>

      <TagPollingIntervalCard />

      <ConnectionsCheckCard />
    </div>
  )
}

export function ConnectivitySection({
  panel,
  onPanelChange
}: {
  panel: ConnectivityPanel
  onPanelChange: (panel: ConnectivityPanel) => void
}) {
  const sightingsEnabled = useFlag(FLAGS.RELEASE_SIGHTING_PLANNER)
  if (panel === 'integrations') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Integrations" onBack={() => onPanelChange('hub')} />
        <IntegrationsTab />
      </div>
    )
  }

  if (panel === 'gateways') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Gateway credentials" onBack={() => onPanelChange('hub')} />
        <GatewaysTab />
      </div>
    )
  }

  if (panel === 'connection-health') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Connection health" onBack={() => onPanelChange('hub')} />
        <ConnectionHealthTab />
      </div>
    )
  }

  if (panel === 'observers' && sightingsEnabled) {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Observers" onBack={() => onPanelChange('hub')} />
        <ObserversTab />
      </div>
    )
  }

  if (panel === 'gateway-link') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Gateway Link routing" onBack={() => onPanelChange('hub')} />
        <GatewayLinkTab />
      </div>
    )
  }

  return <ConnectivityHub onOpen={onPanelChange} />
}
